import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import axios from '../utils/api';

const AdminRefundRequestsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [actionLoadingKey, setActionLoadingKey] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending_or_failed');
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [refundTxnSearch, setRefundTxnSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [refundReason, setRefundReason] = useState('');
  const [transactionDate, setTransactionDate] = useState('');

  const formatAmount = (value) => {
    const numericValue = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  };

  const formatISTDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;

    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'medium',
      hour12: true,
    }).format(parsed);
  };

  const fetchRefundRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await axios.get('/api/orders/refund-requests');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load refund requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefundRequests();
  }, []);

  const isRefundInitiationAllowed = (row) => {
    const queueStatus = getQueueStatus(row);
    const pendingAmount = Number(row?.pending_refund_amount || 0);
    const itemStatuses = (row?.items || []).map((item) => (item.refund_status || '').toLowerCase());
    const hasOpenRefundItems = itemStatuses.some((status) => status !== 'processed' && status !== 'not_required');

    if (queueStatus === 'processed') {
      return false;
    }

    if (pendingAmount <= 0 && !hasOpenRefundItems) {
      return false;
    }

    return true;
  };

  const openInitiateModal = (row) => {
    if (!isRefundInitiationAllowed(row)) {
      setActionError('Refund is already processed for this order.');
      return;
    }

    const itemIds = (row.items || []).map((item) => item.item_id);
    setSelectedRow(row);
    setSelectedItemIds(itemIds);
    setRefundReason('Manual refund initiated by admin/superuser');
    setTransactionDate(new Date().toISOString());
    setShowModal(true);
    setActionError('');
    setActionSuccess('');
  };

  const closeInitiateModal = () => {
    setShowModal(false);
    setSelectedRow(null);
    setSelectedItemIds([]);
    setRefundReason('');
    setTransactionDate('');
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItemIds((prev) => (
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    ));
  };

  const selectedAmount = useMemo(() => {
    if (!selectedRow) return 0;
    return (selectedRow.items || [])
      .filter((item) => selectedItemIds.includes(item.item_id))
      .reduce((sum, item) => sum + Number(item.refund_amount || 0), 0);
  }, [selectedRow, selectedItemIds]);

  const getQueueStatus = (row) => {
    if (row.queue_status) {
      return String(row.queue_status).toLowerCase();
    }

    const itemStatuses = (row.items || []).map((item) => (item.refund_status || '').toLowerCase());
    const paymentStatus = (row.payment_status || '').toLowerCase();
    const latestTxStatus = (row.latest_refund_transaction?.status || '').toLowerCase();

    if (paymentStatus === 'refund_failed' || latestTxStatus === 'failed' || itemStatuses.includes('failed')) {
      return 'failed';
    }

    return 'pending';
  };

  const filteredRows = useMemo(() => {
    const normalizedOrderSearch = orderIdSearch.trim().toLowerCase();
    const normalizedRefundTxnSearch = refundTxnSearch.trim().toLowerCase();

    return rows.filter((row) => {
      const queueStatus = getQueueStatus(row);
      if (statusFilter === 'pending_or_failed') {
        if (!(queueStatus === 'pending' || queueStatus === 'failed')) {
          return false;
        }
      } else if (statusFilter !== 'all' && queueStatus !== statusFilter) {
        return false;
      }

      if (normalizedOrderSearch && !(row.order_id || '').toLowerCase().includes(normalizedOrderSearch)) {
        return false;
      }

      if (normalizedRefundTxnSearch) {
        const latestRefundId = (row.latest_refund_transaction?.razorpay_refund_transaction_id || '').toLowerCase();
        const anyRefundId = (row.razorpay_refund_transaction_ids || []).some((refundId) =>
          String(refundId || '').toLowerCase().includes(normalizedRefundTxnSearch)
        );
        if (!latestRefundId.includes(normalizedRefundTxnSearch) && !anyRefundId) {
          return false;
        }
      }

      return true;
    });
  }, [rows, statusFilter, orderIdSearch, refundTxnSearch]);

  const handleInitiateRefund = async () => {
    if (!selectedRow) return;

    const reason = refundReason.trim();
    if (!reason) {
      setActionError('Refund reason is required.');
      return;
    }
    if (selectedItemIds.length === 0) {
      setActionError('Select at least one item for refund.');
      return;
    }

    const loadingKey = `initiate-${selectedRow.order_id}`;
    setActionLoadingKey(loadingKey);
    setActionError('');
    setActionSuccess('');

    try {
      const payload = {
        reason,
        item_ids: selectedItemIds,
        transaction_date: transactionDate || new Date().toISOString(),
      };
      const { data } = await axios.post(`/api/orders/${selectedRow.order_id}/initiate-refund`, payload);
      setActionSuccess(
        `Refund initiated. Payment Txn ID: ${data.payment_transaction_id || '-'}, Razorpay Refund Txn ID: ${data.razorpay_refund_transaction_id || '-'}, Date: ${formatISTDateTime(data.transaction_date)}, Amount: ${selectedRow.currency} ${formatAmount(data.transaction_amount)}.`
      );
      closeInitiateModal();
      fetchRefundRequests();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to initiate refund');
    } finally {
      setActionLoadingKey('');
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading refund requests...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Refund Requests</h1>
          <p className="text-muted mb-0">Track all refunds and initiate Razorpay refunds with reason capture.</p>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {actionError && <Alert variant="danger" dismissible onClose={() => setActionError('')}>{actionError}</Alert>}
      {actionSuccess && <Alert variant="success" dismissible onClose={() => setActionSuccess('')}>{actionSuccess}</Alert>}

      {rows.length > 0 && (
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label className="mb-1">Status</Form.Label>
                  <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                    <option value="pending_or_failed">Pending or Failed (Default)</option>
                    <option value="failed">Failed only</option>
                    <option value="pending">Pending only</option>
                    <option value="processed">Processed only</option>
                    <option value="all">All statuses</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label className="mb-1">Order ID Search</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by order ID"
                    value={orderIdSearch}
                    onChange={(event) => setOrderIdSearch(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label className="mb-1">Razorpay Refund Txn ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by refund txn ID"
                    value={refundTxnSearch}
                    onChange={(event) => setRefundTxnSearch(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={2} className="text-md-end">
                <div className="small text-muted">Showing {filteredRows.length} of {rows.length} requests</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {rows.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <h5>No refunds found</h5>
            <p className="text-muted mb-0">Refund records and refund requests will appear here.</p>
          </Card.Body>
        </Card>
      ) : filteredRows.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <h5>No results for current filters</h5>
            <p className="text-muted mb-0">Try changing status filter or clearing order/refund transaction search.</p>
          </Card.Body>
        </Card>
      ) : (
        <Table responsive bordered hover>
          <thead>
            <tr>
              <th>Order</th>
              <th>Buyer</th>
              <th>Refund Queue Items</th>
              <th>Pending Amount</th>
              <th>Latest Refund Transaction</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.order_id}>
                <td>
                  <div className="fw-semibold">#{row.order_id}</div>
                  <div className="text-muted small">Invoice #{row.invoice_number || '-'}</div>
                  <div className="mt-1">
                    <Badge bg={row.payment_status === 'refund_failed' ? 'danger' : 'warning'}>
                      {row.payment_status || 'payment_pending'}
                    </Badge>
                    <Badge bg={getQueueStatus(row) === 'failed' ? 'danger' : getQueueStatus(row) === 'processed' ? 'success' : 'secondary'} className="ms-2">
                      {getQueueStatus(row)}
                    </Badge>
                  </div>
                </td>
                <td>
                  <div>{row.buyer_name || '-'}</div>
                  <div className="text-muted small">{row.buyer_email || '-'}</div>
                </td>
                <td>
                  {(row.items || []).map((item) => (
                    <div key={item.item_id} className="mb-2 pb-2 border-bottom">
                      <div className="small fw-semibold">{item.product_name || item.product_id}</div>
                      <div className="small text-muted">Item #{item.item_id}</div>
                      <div className="small text-muted">Qty: {item.quantity} | SKU: {item.variant_sku || '-'}</div>
                      <div className="small">
                        <Badge bg={item.refund_status === 'failed' ? 'danger' : 'secondary'}>
                          {item.refund_status || 'requested'}
                        </Badge>
                        <span className="ms-2 text-muted">{row.currency} {formatAmount(item.refund_amount)}</span>
                      </div>
                    </div>
                  ))}
                </td>
                <td className="fw-semibold">
                  <div>{row.currency} {formatAmount(row.pending_refund_amount)}</div>
                  <div className="small text-muted">Total: {row.currency} {formatAmount(row.total_refund_amount || 0)}</div>
                </td>
                <td>
                  {row.latest_refund_transaction ? (
                    <>
                      <div className="small"><strong>Txn ID:</strong> {row.latest_refund_transaction.payment_transaction_id || '-'}</div>
                      <div className="small"><strong>Razorpay Refund ID:</strong> {row.latest_refund_transaction.razorpay_refund_transaction_id || '-'}</div>
                      <div className="small"><strong>Date:</strong> {formatISTDateTime(row.latest_refund_transaction.transaction_date)}</div>
                      <div className="small"><strong>Amount:</strong> {row.currency} {formatAmount(row.latest_refund_transaction.transaction_amount)}</div>
                      <div className="small"><strong>Status:</strong> {row.latest_refund_transaction.status || '-'}</div>
                      {row.latest_refund_transaction.reason && (
                        <div className="small"><strong>Reason:</strong> {row.latest_refund_transaction.reason}</div>
                      )}
                      {row.latest_refund_transaction.error_message && (
                        <div className="small text-danger"><strong>Error:</strong> {row.latest_refund_transaction.error_message}</div>
                      )}
                    </>
                  ) : (
                    <span className="text-muted">No prior refund transaction</span>
                  )}
                </td>
                <td>
                  {(() => {
                    const canInitiateRefund = isRefundInitiationAllowed(row);
                    return (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={actionLoadingKey === `initiate-${row.order_id}` || !canInitiateRefund}
                    title={canInitiateRefund ? 'Initiate refund' : 'Refund already processed'}
                    onClick={() => openInitiateModal(row)}
                  >
                    {actionLoadingKey === `initiate-${row.order_id}` ? <Spinner size="sm" animation="border" /> : 'Initiate Refund'}
                  </Button>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={closeInitiateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Initiate Refund</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRow && (
            <>
              <p className="mb-2">
                <strong>Order:</strong> #{selectedRow.order_id}
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Select Items</Form.Label>
                {(selectedRow.items || []).map((item) => (
                  <Form.Check
                    key={item.item_id}
                    type="checkbox"
                    id={`refund-item-${item.item_id}`}
                    label={`${item.product_name || item.product_id} (Qty ${item.quantity}) - ${selectedRow.currency} ${formatAmount(item.refund_amount)}`}
                    checked={selectedItemIds.includes(item.item_id)}
                    onChange={() => toggleItemSelection(item.item_id)}
                    className="mb-1"
                  />
                ))}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Refund Reason</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={refundReason}
                  onChange={(event) => setRefundReason(event.target.value)}
                  placeholder="Enter refund reason"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Transaction Date (default now)</Form.Label>
                <Form.Control
                  type="text"
                  value={formatISTDateTime(transactionDate)}
                  readOnly
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Transaction Amount</Form.Label>
                <Form.Control
                  type="text"
                  value={`${selectedRow.currency} ${formatAmount(selectedAmount)}`}
                  readOnly
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeInitiateModal}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleInitiateRefund}
            disabled={!selectedRow || actionLoadingKey === `initiate-${selectedRow?.order_id}`}
          >
            {selectedRow && actionLoadingKey === `initiate-${selectedRow.order_id}` ? 'Initiating...' : 'Initiate Refund'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminRefundRequestsPage;
